<script setup lang="ts">
import IconMaterialSymbolsCheck from '~icons/material-symbols/check'
import IconMaterialSymbolsWidgets from '~icons/material-symbols/widgets-outline'
import { useModuleAccess } from '@/shared/composables/useModuleAccess'
import { useToolStore } from '@/stores/tool'
import { useRouter } from 'vue-router'

import { Card } from '@/components/ui/card'
import Icon from '@/shared/components/Icon.vue'

import { TOOLS } from '@/shared/utils/tools'
import { OperationalModel } from '§M/types/enumerated.type'

const router = useRouter()
const toolStore = useToolStore()
const { hasModuleAccess } = useModuleAccess()

const selectTool = (model: OperationalModel) => {
  if (!hasModuleAccess(model)) return
  toolStore.setTool(model)
  const redirect = router.currentRoute.value.query.redirect as string | undefined
  router.push(redirect || '/projects')
}

definePage({
  meta: {
    label: 'Choose a tool',
    icon: IconMaterialSymbolsWidgets
  }
})
</script>

<template>
  <div class="mx-auto w-full max-w-4xl px-5 py-10">
    <h1 class="text-foreground text-2xl font-bold">Choose a tool</h1>
    <p class="text-muted-foreground mt-1 text-sm">
      Nexus and Axios solve different problems. Projects, locations and concepts are scoped to the one you pick.
    </p>

    <div class="mt-8 grid gap-6 sm:grid-cols-2">
      <Card
        v-for="tool in TOOLS"
        :key="tool.model"
        :class="[
          'gap-0 py-6 transition-colors',
          hasModuleAccess(tool.model)
            ? 'cursor-pointer hover:border-primary/60'
            : 'cursor-not-allowed opacity-60'
        ]"
        role="button"
        :aria-disabled="!hasModuleAccess(tool.model)"
        @click="selectTool(tool.model)"
      >
        <div class="flex flex-col gap-4 px-6">
          <h2 class="text-foreground flex items-center gap-2 text-lg font-bold">
            <Icon
              :icon="tool.icon"
              class="h-5 w-5 shrink-0"
            />
            {{ tool.title }}
          </h2>

          <p class="text-primary text-sm font-semibold">{{ tool.tagline }}</p>
          <p class="text-muted-foreground text-sm">{{ tool.description }}</p>

          <ul class="flex flex-col gap-1.5">
            <li
              v-for="feature in tool.features"
              :key="feature"
              class="text-muted-foreground flex items-center gap-2 text-sm"
            >
              <Icon
                :icon="IconMaterialSymbolsCheck"
                severity="primary"
                class="h-4 w-4 shrink-0"
              />
              {{ feature }}
            </li>
          </ul>

          <p
            v-if="!hasModuleAccess(tool.model)"
            class="text-muted-foreground text-xs"
          >
            Your organization is not subscribed to this module.
          </p>
        </div>
      </Card>
    </div>
  </div>
</template>
